import { useCallback } from 'react';

import { batch } from 'react-redux';
import { toast } from 'react-toastify';

import { format } from 'date-fns';
import { useAppDispatch, useAppSelector } from 'redux/store/store.types';
import { getApiErrorMessage } from 'utils';

import { EEecsFeesType } from 'modules/eecs-go';

import {
  useLazyExportCancellationsQuery,
  useLazyExportFeesQuery,
  useLazyExportForwardsQuery,
  useLazyExportPackagesQuery,
  useLazyExportPortfolioQuery,
  useLazyExportProductionDevicesQuery,
  useLazyExportTransactionsQuery,
  useLazyExportWalletTransactionsQuery,
} from '../api/data-export.api';
import { setDataExportModal } from '../store/data-export.slice';
import { EAppDataExportFileTypes, EAppDataExportPages, IAppDataExportModal } from '../types/data-export.types';

interface OpenModal {
  modal: IAppDataExportModal;
}

interface UseAppDataExport {
  openDataExportModal: (payload: OpenModal) => void;
  closeDataExportModal: () => void;
  exportData: (page: EAppDataExportPages, fileType: EAppDataExportFileTypes) => Promise<boolean>;
}

export const useAppDataExport = (): UseAppDataExport => {
  const dispatch = useAppDispatch();
  const { filters: walletFilters, sorting: walletSorting } = useAppSelector((state) => state.wallet);
  const { filters: certificateFilters, sorting: certificateSorting } = useAppSelector(
    (state) => state.eecsGoCertificate,
  );
  const { filters: forwardsFilters, sorting: forwardsSorting } = useAppSelector((state) => state.eecsForward);
  const { filters: packagesFilters, sorting: packagesSorting } = useAppSelector((state) => state.eecsPackage);
  const { filters: transactionsFilters, sorting: transactionsSorting } = useAppSelector(
    (state) => state.eecsTransaction,
  );
  const { filters: productionDevicesFilters, sorting: productionDevicesSorting } = useAppSelector(
    (state) => state.productionDevice,
  );

  const { filters: cancellationsFilters, sorting: cancellationsSorting } = useAppSelector(
    (state) => state.eecsCancellations,
  );
  const { filters: feesFilters, sorting: feesSorting } = useAppSelector((state) => state.eecsFees);
  const { currentUser } = useAppSelector((state) => state.authSession);

  const companyName = currentUser?.company?.name.toLocaleLowerCase().replace(/ /g, '_');

  const [exportPortfolioQuery] = useLazyExportPortfolioQuery();
  const [exportForwardsQuery] = useLazyExportForwardsQuery();
  const [exportPackagesQuery] = useLazyExportPackagesQuery();
  const [exportTransactionsQuery] = useLazyExportTransactionsQuery();
  const [exportProductionDevicesQuery] = useLazyExportProductionDevicesQuery();

  const [exportCancellationsQuery] = useLazyExportCancellationsQuery();
  const [exportWalletTransactionsQuery] = useLazyExportWalletTransactionsQuery();
  const [exportFeesQuery] = useLazyExportFeesQuery();

  const downloadFile = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.style.cssText = 'display: none';
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const exportPortfolioHandler = useCallback(
    async (fileType: EAppDataExportFileTypes) => {
      const { data: response, error } = await exportPortfolioQuery({
        page: 1,
        per_page: 1000,
        file_type: fileType,
        search: certificateFilters?.search,
        filter_by_production_device_id: certificateFilters?.pdId,
        filter_by_has_comment:
          certificateFilters?.hasComment === 1 ? true : certificateFilters?.hasComment === 0 ? false : null,
        filter_by_has_exchanges:
          certificateFilters?.hasExchanges === 1 ? true : certificateFilters?.hasExchanges === 0 ? false : null,
        filter_by_prod_start_date: certificateFilters?.prodStartDate,
        filter_by_prod_end_date: certificateFilters?.prodEndDate,
        filter_by_exp_start_date: certificateFilters?.expStartDate,
        filter_by_exp_end_date: certificateFilters?.expEndDate,
        filter_by_energy_source: certificateFilters?.energySource,
        filter_by_type_of_installation: certificateFilters?.productionTechnology,
        filter_by_type_of_support: certificateFilters?.typeOfSupport,
        filter_by_aib_grid_connected: certificateFilters?.aibGridConnected,
        filter_by_registry: certificateFilters?.registry,
        filter_by_production_date: certificateFilters?.productionYear
          ? format(new Date(certificateFilters.productionYear), 'yyyy-MM-dd')
          : null,
        order_by_bundle_id: certificateSorting?.orderBundleId,
        order_by_production_device_name: certificateSorting?.orderProductionDeviceName,
        order_by_volume: certificateSorting?.orderVolume,
        order_by_comment: certificateSorting?.orderComment,
      });

      const isError = error || !response;
      if (isError) {
        toast.error(getApiErrorMessage(error) || 'Failed to export EECS Inventory!');
      } else {
        const fileName = `${companyName}_inventory_${format(new Date(), 'yyyyMMdd-HHmmss')}.${fileType}`;
        downloadFile(response, fileName);
      }

      return !isError;
    },
    [certificateFilters, certificateSorting, currentUser],
  );

  const exportForwardsHandler = useCallback(
    async (fileType: EAppDataExportFileTypes) => {
      const { data: response, error } = await exportForwardsQuery({
        per_page: 1000,
        page: 1,
        file_type: fileType,
        search: forwardsFilters?.search,
        filter_by_has_exchanges: forwardsFilters?.onExchange ?? null,
        filter_by_energy_source: forwardsFilters?.energySource,
        filter_by_type_of_installation: forwardsFilters?.productionTechnology,
        filter_by_delivery_start_date: forwardsFilters?.deliveryStartDate,
        filter_by_delivery_end_date: forwardsFilters?.deliveryEndDate,
        filter_by_counter_party_necs_account_id: forwardsFilters?.necsCompanyId,
        filter_by_status: forwardsFilters?.statuses,
        filter_by_type: forwardsFilters?.types,
        filter_by_aib_grid_connected: forwardsFilters?.aibGridConnected,
        filter_by_type_of_support: forwardsFilters?.typeOfSupport,
        filter_by_production_date: forwardsFilters?.productionYear
          ? format(new Date(forwardsFilters.productionYear), 'yyyy-MM-dd')
          : null,

        order_by_delivery_date: forwardsSorting?.orderDeliveryDate,
        order_by_volume: forwardsSorting?.orderVolume,
        order_by_counter_party_company_name: forwardsSorting?.orderNecsCompanyName,
        order_by_type: forwardsSorting?.orderType,
      });

      const isError = error || !response;
      if (isError) {
        toast.error(getApiErrorMessage(error) || 'Failed to export NECS Forwards!');
      } else {
        const fileName = `${companyName}_forwards_${format(new Date(), 'yyyyMMdd-HHmmss')}.${fileType}`;
        downloadFile(response, fileName);
      }

      return !isError;
    },
    [forwardsFilters, forwardsSorting],
  );

  const exportPackagesHandler = useCallback(
    async (fileType: EAppDataExportFileTypes) => {
      const { data: response, error } = await exportPackagesQuery({
        per_page: 1000,
        page: 1,
        search: packagesFilters?.search,
        file_type: fileType,
        filter_by_creation_start_date: packagesFilters?.creationStartDate,
        filter_by_creation_end_date: packagesFilters?.creationEndDate,
        filter_by_type_of_installation: packagesFilters?.productionTechnology,
        filter_by_energy_source: packagesFilters?.energySource,
        filter_by_counter_party_necs_account_id: packagesFilters?.necsCompanyId,
        filter_by_status: packagesFilters?.statuses,
        filter_by_type: packagesFilters?.types,
        filter_by_contents: packagesFilters?.contents,

        order_by_price: packagesSorting?.orderPrice,
        order_by_counter_party_company_name: packagesSorting?.orderNecsCompanyName,
        order_by_type: packagesSorting?.orderType,
        order_by_contents: packagesSorting?.orderContents,
      });

      const isError = error || !response;
      if (isError) {
        toast.error(getApiErrorMessage(error) || 'Failed to export NECS Packages!');
      } else {
        const fileName = `${companyName}_packages_${format(new Date(), 'yyyyMMdd-HHmmss')}.${fileType}`;
        downloadFile(response, fileName);
      }

      return !isError;
    },
    [packagesFilters, packagesSorting],
  );

  const exportTransactionsHandler = useCallback(
    async (fileType: EAppDataExportFileTypes) => {
      const { data: response, error } = await exportTransactionsQuery({
        per_page: 1000,
        page: 1,
        file_type: fileType,
        order_transaction_type: transactionsSorting.orderTransactionType,
        order_txn_id: transactionsSorting.orderTxnId,
        order_transaction_date: transactionsSorting.orderTransactionDate,
        order_buyer_necs_company_name: transactionsSorting.orderBuyerNecsCompanyName,
        search: transactionsFilters?.search || null,
        transaction_type: transactionsFilters?.transactionType || null,
        start_date: transactionsFilters?.startDate || null,
        end_date: transactionsFilters?.endDate || null,
        transaction_status: transactionsFilters?.transactionStatus || null,
      });

      const isError = error || !response;
      if (isError) {
        toast.error(getApiErrorMessage(error) || 'Failed to export NECS Transactions!');
      } else {
        const fileName = `${companyName}_transactions_${format(new Date(), 'yyyyMMdd-HHmmss')}.${fileType}`;
        downloadFile(response, fileName);
      }

      return !isError;
    },
    [transactionsFilters, transactionsSorting],
  );

  const exportProductionDevicesHandler = useCallback(
    async (fileType: EAppDataExportFileTypes) => {
      const { data: response, error } = await exportProductionDevicesQuery({
        page: 1,
        per_page: 1000,
        file_type: fileType,
        order_volume: productionDevicesSorting?.orderVolume,
        order_description: productionDevicesSorting?.orderDescription,
        order_pd_id: productionDevicesSorting?.orderPDId,
        search: productionDevicesFilters?.search,
        is_owner:
          productionDevicesFilters?.isOwner === 1 ? true : productionDevicesFilters?.isOwner === 0 ? false : null,
        has_certificates:
          productionDevicesFilters?.hasCertificates === 1
            ? true
            : productionDevicesFilters?.hasCertificates === 0
              ? false
              : null,
      });

      const isError = error || !response;
      if (isError) {
        toast.error(getApiErrorMessage(error) || 'Failed to export NECS Production Devices!');
      } else {
        downloadFile(response, `necs_production_devices_export.${fileType}`);
      }

      return !isError;
    },
    [productionDevicesFilters, productionDevicesSorting],
  );

  const exportCancellationsHandler = useCallback(
    async (fileType: EAppDataExportFileTypes) => {
      const { data: response, error } = await exportCancellationsQuery({
        per_page: 1000,
        page: 1,
        file_type: fileType,
        search: cancellationsFilters?.search,
        filter_by_creation_start_date: cancellationsFilters?.creationStartDate,
        filter_by_creation_end_date: cancellationsFilters?.creationEndDate,
        filter_by_energy_source: cancellationsFilters?.energySource,
        filter_by_type_of_installation: cancellationsFilters?.productionTechnology,
        order_by_beneficiary_name: cancellationsSorting?.orderBeneficiaryName,
        order_by_contents_volume: cancellationsSorting?.orderContentsVolume,
        order_by_comment: cancellationsSorting?.orderComment,
      });

      const isError = error || !response;
      if (isError) {
        toast.error(getApiErrorMessage(error) || 'Failed to export EECS Cancellations!');
      } else {
        const fileName = `${companyName}_cancellations_${format(new Date(), 'yyyyMMdd-HHmmss')}.${fileType}`;
        downloadFile(response, fileName);
      }

      return !isError;
    },
    [cancellationsSorting, cancellationsFilters],
  );

  const exportWalletTransactionsHandler = useCallback(
    async (fileType: EAppDataExportFileTypes) => {
      const { data: response, error } = await exportWalletTransactionsQuery({
        per_page: 1000,
        page: 1,
        file_type: fileType,
        search: cancellationsFilters?.search,
        filter_by_type: walletFilters?.type,
        filter_by_creation_start_date: walletFilters?.creationStartDate,
        filter_by_creation_end_date: walletFilters?.creationEndDate,
      });

      const isError = error || !response;
      if (isError) {
        toast.error(getApiErrorMessage(error) || 'Failed to export Wallet Transactions!');
      } else {
        const fileName = `${companyName}_wallet_transactions_${format(new Date(), 'yyyyMMdd-HHmmss')}.${fileType}`;
        downloadFile(response, fileName);
      }

      return !isError;
    },
    [walletFilters, walletSorting],
  );

  const exportFeesHandler = useCallback(
    async (fileType: EAppDataExportFileTypes) => {
      const { data: response, error } = await exportFeesQuery({
        per_page: 1000,
        page: 1,
        file_type: fileType,
        filter_by_type: feesFilters?.type ?? EEecsFeesType.CERTIFICATE,
        search: feesFilters?.search,
        filter_by_payment_type: feesFilters?.paymentType,
        filter_by_counter_party_company_id: feesFilters?.companyId,
        filter_by_status: feesFilters?.status,
        filter_by_transaction_start_date: feesFilters?.creationStartDate,
        filter_by_transaction_end_date: feesFilters?.creationEndDate,
      });

      const isError = error || !response;
      if (isError) {
        toast.error(getApiErrorMessage(error) || 'Failed to export Platform Fees!');
      } else {
        const fileName = `${companyName}_fees_${format(new Date(), 'yyyyMMdd-HHmmss')}.${fileType}`;
        downloadFile(response, fileName);
      }

      return !isError;
    },
    [feesFilters, feesSorting],
  );

  const exportData = async (page: EAppDataExportPages, fileType: EAppDataExportFileTypes) => {
    switch (page) {
      case EAppDataExportPages.FORWARDS:
        return await exportForwardsHandler(fileType);
      case EAppDataExportPages.PACKAGES:
        return await exportPackagesHandler(fileType);
      case EAppDataExportPages.SPOT:
        return await exportPortfolioHandler(fileType);
      case EAppDataExportPages.TRANSACTIONS:
        return await exportTransactionsHandler(fileType);
      case EAppDataExportPages.PRODUCTION_DEVICES:
        return await exportProductionDevicesHandler(fileType);
      case EAppDataExportPages.CANCELLATIONS:
        return await exportCancellationsHandler(fileType);
      case EAppDataExportPages.WALLET:
        return await exportWalletTransactionsHandler(fileType);
      case EAppDataExportPages.FEES:
        return await exportFeesHandler(fileType);
      default:
        return false;
    }
  };

  const openDataExportModal = ({ modal }: OpenModal) => {
    batch(() => {
      dispatch(setDataExportModal(modal));
    });
  };

  const closeDataExportModal = () => {
    dispatch(setDataExportModal(null));
  };

  return { openDataExportModal, closeDataExportModal, exportData };
};
